import saveAs from 'file-saver';
import {ToastrService} from 'ngx-toastr';

export type ImportErrorRow = {
    row: number;
    vehicleNumber?: string;
    field?: string;
    message: string;
};

export type BulkImportResultView = {
    totalImported: number;
    totalFailed: number;
    errors: ImportErrorRow[];
    failedRowsCsvBase64: string | null;
    failedRowsFilename: string;
};

export function countFailedRows(errors: ImportErrorRow[]): number {
    const rowNumbers = new Set(errors.filter(error => error.row > 0).map(error => error.row));
    if (rowNumbers.size > 0) {
        return rowNumbers.size;
    }
    return errors.length;
}

export function parseBulkImportFailure(err: any, defaultFilename: string): BulkImportResultView | null {
    const payload = err?.error;
    if (!payload?.errors?.length) {
        return null;
    }

    const errors: ImportErrorRow[] = payload.errors.map((row: any) => ({
        row: row.row,
        vehicleNumber: row.vehicle_number,
        field: row.field,
        message: row.message,
    }));

    return {
        totalImported: Number(payload.total_imported ?? payload.uploaded_records ?? 0),
        totalFailed: Number(payload.total_failed ?? countFailedRows(errors)),
        errors,
        failedRowsCsvBase64: payload.failed_rows_csv_base64 ?? null,
        failedRowsFilename: payload.failed_rows_filename || defaultFilename,
    };
}

export function downloadFailureCsvFromBase64(base64: string, filename: string): void {
    const blob = base64ToBlob(base64, 'text/csv');
    saveAs(blob, filename);
}

export function handleBulkImportFallbackError(err: any, toastr: ToastrService): void {
    if (err?.error?.non_field_errors?.length) {
        err.error.non_field_errors.forEach((message: string) => toastr.error(message, 'Error'));
        return;
    }
    if (err?.error?.error) {
        toastr.error(err.error.error, 'Error');
        return;
    }
    if (err?.error?.detail) {
        toastr.error(err.error.detail, 'Error');
        return;
    }
    if (err?.status !== 403 && err?.status !== 0) {
        toastr.error('Something went wrong!', 'Error');
    }
}

export function handleBulkImportError(
    err: any,
    toastr: ToastrService,
    defaultFilename: string,
): BulkImportResultView | null {
    const result = parseBulkImportFailure(err, defaultFilename);
    if (result) {
        return result;
    }
    handleBulkImportFallbackError(err, toastr);
    return null;
}

function base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], {type: contentType});
}
